import { useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Target, Sparkles, Copy, Share2 } from "lucide-react";
import { Markdown } from "../components/Markdown";
import { toast } from "sonner";

const ROLES = ["Chief Executive Officer","Chief Operating Officer","Chief Financial Officer","Chief Technology Officer","Chief Marketing Officer","Chief Product Officer","Chief People Officer","Chief Revenue Officer","Chief AI Officer","VP Engineering","Head of Sales","General Counsel"];
const STAGES = ["Pre-seed","Seed","Series A","Series B","Growth","Public"];

export default function Practice() {
  const [role, setRole] = useState("Chief Technology Officer");
  const [industry, setIndustry] = useState("");
  const [stage, setStage] = useState("Series A");
  const [ctx, setCtx] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const share = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/practice/plans/${out.plan_id}/share`);
      const url = `${window.location.origin}/p/${data.slug}`;
      setShareUrl(url);
      navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Public link created and copied");
    } catch { toast.error("Could not create link"); }
    finally { setSharing(false); }
  };

  const gen = async () => {
    setBusy(true); setOut(null); setShareUrl("");
    try {
      const { data } = await api.post("/practice/plan_30_60_90", {
        role_title: role, industry: industry || null, stage, context: ctx || null,
      });
      setOut(data);
    } catch { toast.error("Plan generation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-10" data-testid="practice-page">
      <div>
        <div className="label-caps mb-2 flex items-center gap-1.5"><Target className="h-3 w-3 text-emerald-400" />Pillar · Practice</div>
        <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">Run the operating system.</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Generate a grounded 30 · 60 · 90 day plan for any executive role, drawn from the tribunal library.
        </p>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        <Card className="p-6 md:col-span-2 space-y-4">
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="practice-role" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stage</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger data-testid="practice-stage" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Industry (optional)</Label>
            <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="e.g. Fintech, Healthcare, AI/ML" className="mt-1.5" data-testid="practice-industry" />
          </div>
          <div>
            <Label>Context (optional)</Label>
            <Textarea rows={4} value={ctx} onChange={(e) => setCtx(e.target.value)}
              placeholder="Team shape, inherited problems, mandate, constraints…" className="mt-1.5" data-testid="practice-context" />
          </div>
          <Button onClick={gen} disabled={busy} className="rounded-full w-full" data-testid="practice-generate">
            <Sparkles className="h-4 w-4 mr-1" />{busy ? "Composing plan…" : "Generate 30-60-90"}
          </Button>
        </Card>

        <Card className="p-6 md:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <div className="label-caps">Grounded plan</div>
            {out?.content && (
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(out.content); toast.success("Copied"); }} data-testid="practice-copy">
                  <Copy className="h-3.5 w-3.5 mr-1" />Copy
                </Button>
                {out.plan_id && (
                  <Button size="sm" className="rounded-full" onClick={share} disabled={sharing} data-testid="practice-share">
                    <Share2 className="h-3.5 w-3.5 mr-1" />{sharing ? "Creating link…" : "Share"}
                  </Button>
                )}
              </div>
            )}
          </div>
          {busy ? (
            <div className="text-muted-foreground text-sm">Searching the tribunal library and composing…</div>
          ) : out ? (
            <>
              {shareUrl && (
                <div className="mb-5 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="practice-share-box">
                  <Input readOnly value={shareUrl} className="h-8 text-xs font-mono" data-testid="practice-share-url" />
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }} data-testid="practice-share-copy"><Copy className="h-3.5 w-3.5" /></Button>
                </div>
              )}
              <div className="max-h-[600px] overflow-y-auto"><Markdown text={out.content} testId="practice-output" /></div>
              {out.sources_used?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-border">
                  <div className="label-caps mb-2">Sources used</div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    {out.sources_used.map((s, i) => (
                      <div key={i} className="font-mono">· {s.title}</div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-muted-foreground text-sm">Fill the form and generate a plan grounded in the tribunal's chapters on cadence, decision rights and first-90-days practice.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
